import {
  ACTIVITY_EVENTS,
  APP_ROUTES,
  COUNTDOWN_SECONDS,
  IDLE_DISABLED_ROUTES,
  IDLE_MODAL_STATES,
  IDLE_TIMEOUT,
  PATIENT_NAVIGATOR_IDLE_TIMEOUT,
} from "@src/constants";
import { useNavigateWithUTM } from "@src/hooks/useNavigateWithUTM";
import { setBypassBeforeUnload } from "@src/hooks/useNavigationBlocker";
import ClientLogsService from "@src/services/client-logs.service";
import { unfreezeAppointment } from "@src/services/unfreeze-appointment.service";
import logger from "@src/utils/logger";
import { isValidPositiveNumber } from "@src/utils/number-utils";
import { resolveProviderSelectionPath } from "@src/utils/provider-back-path";
import { canRoleBypassGuardrails } from "@src/utils/role-guardrails";
import { useLocation } from "@uhg-abyss/web/tools/reactRouterTools";
import type { FC, ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useApplicationContext } from "./ApplicationContext";
import { useEnvironment } from "./EnvironmentContext";
import { useTimerContext } from "./TimerContext";

type IdleModalState = (typeof IDLE_MODAL_STATES)[keyof typeof IDLE_MODAL_STATES];

export interface IdleTimerContextType {
  modalState: IdleModalState;
  countdown: number;
  isIdleTimerDisabled: boolean;
  hasAppointmentHold: boolean;
  onStayInSession: () => void;
  onEndSession: () => void;
  onReReserve: () => Promise<void>;
  onDismissAndEndSession: () => void;
  onAcknowledgeTimeout: () => void;
  /** Register a draft-save callback invoked when the user clicks "I'm here".
   *  The callback should flush the active form's local state to ApplicationContext
   *  without running validation. Only one callback is held at a time — the
   *  currently-mounted form — and it is cleared on form unmount. */
  registerFlushCallback: (cb: () => void) => void;
  /** Remove the previously registered flush callback (call on form unmount). */
  unregisterFlushCallback: () => void;
  /** Returns true while endSessionAndRedirect is in progress so that
   *  useIdleFormFlush can skip unmount-flushing during a session clear,
   *  preventing re-population of sessionData after clearSession(). */
  checkIsSessionEnding: () => boolean;
}

const IdleTimerContext = createContext<IdleTimerContextType | undefined>(undefined);

const IDLE_PHASE_KEY = "idlePhase";
const COUNTDOWN_DEADLINE_KEY = "idleCountdownDeadline";
// Human-readable companion of COUNTDOWN_DEADLINE_KEY, written for easier
// inspection during testing/debugging. The numeric ms key above remains the
// single source of truth for all timing comparisons.
const COUNTDOWN_DEADLINE_READABLE_KEY = "idleCountdownDeadlineReadable";

/** Format an epoch-ms timestamp as an ISO string for the readable debug keys. */
const toReadableTimestamp = (epochMs: number): string => {
  try {
    return new Date(epochMs).toISOString();
  } catch {
    return String(epochMs);
  }
};

// Read once on mount — only restore 'timeout', everything else resets to 'active'
const getInitialModalState = (isPatientNavigator: boolean): IdleModalState => {
  if (isPatientNavigator) {
    return IDLE_MODAL_STATES.ACTIVE;
  }

  try {
    const stored = localStorage.getItem(IDLE_PHASE_KEY) as IdleModalState | null;
    if (stored === IDLE_MODAL_STATES.TIMEOUT) return IDLE_MODAL_STATES.TIMEOUT;
  } catch {
    /* ignore SSR / private browsing */
  }
  return IDLE_MODAL_STATES.ACTIVE;
};

/** Resolve the effective idle timeout for a given role and env configuration. */
const resolveIdleTimeout = (
  isPatientNavigator: boolean,
  envIdleTimeout: unknown,
  envPatientNavigatorIdleTimeout: unknown,
): number => {
  if (isPatientNavigator) {
    return isValidPositiveNumber(envPatientNavigatorIdleTimeout)
      ? envPatientNavigatorIdleTimeout
      : PATIENT_NAVIGATOR_IDLE_TIMEOUT;
  }
  return isValidPositiveNumber(envIdleTimeout) ? envIdleTimeout : IDLE_TIMEOUT;
};

// ─── Logging helper ──────────────────────────────────────────────
const logAction = (userUuid: string | undefined, action: string, message: string) => {
  ClientLogsService.logWebAction(userUuid ?? "unknown", `IdleTimerContext.${action}`, message);
};

const logClick = (userUuid: string | undefined, buttonId: string, message: string) => {
  ClientLogsService.logClick(userUuid ?? "unknown", "IdleTimerContext", buttonId, message);
};

// ─── Provider ────────────────────────────────────────────────────
export const IdleTimerProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { data, setData, clearSession, clearAppointmentSelection } = useApplicationContext();
  const {
    IDLE_TIMEOUT: ENV_IDLE_TIMEOUT,
    COUNTDOWN_SECONDS: ENV_COUNTDOWN_SECONDS,
    PATIENT_NAVIGATOR_IDLE_TIMEOUT: ENV_PATIENT_NAVIGATOR_IDLE_TIMEOUT,
  } = useEnvironment();
  const { isTimerRunning, stopTimer } = useTimerContext();
  const location = useLocation();
  const pathname = location.pathname;
  const navigate = useNavigateWithUTM();
  const isPatientNavigator = canRoleBypassGuardrails(data.userRole);
  // Activity tracking keys scoped by lead to prevent cross-lead contamination
  const leadScopedId = data.salesforceLeadId?.trim();
  const LAST_ACTIVITY_KEY = leadScopedId ? `lastActivityAt:${leadScopedId}` : "lastActivityAt";
  const LAST_ACTIVITY_READABLE_KEY = `${LAST_ACTIVITY_KEY}Readable`;
  const SESSION_EXPIRY_KEY = leadScopedId
    ? `sessionExpirySeconds:${leadScopedId}`
    : "sessionExpirySeconds";
  const isIdleTimerDisabled =
    IDLE_DISABLED_ROUTES.includes(pathname) ||
    (pathname === APP_ROUTES.GET_STARTED && !data.hasGetStartedFormInput && !data.isReferrerFlow) ||
    (pathname === APP_ROUTES.REFER && !data.hasReferFormInput);
  const hasAppointmentHold = isTimerRunning ?? false;

  // ─── Flush callback ref (form draft-save on "I'm here") ────────
  const flushCallbackRef = useRef<(() => void) | null>(null);
  // Set to true for the duration of endSessionAndRedirect so useIdleFormFlush
  // can skip its flushOnUnmount path and avoid re-populating cleared session data.
  const isSessionEndingRef = useRef(false);

  const registerFlushCallback = useCallback((cb: () => void) => {
    flushCallbackRef.current = cb;
  }, []);

  const unregisterFlushCallback = useCallback(() => {
    flushCallbackRef.current = null;
  }, []);

  // Stryker disable next-line ArrayDeclaration -- equivalent mutation: [] and ["Stryker was here"] are both stable literals, useCallback behaviour is identical
  const checkIsSessionEnding = useCallback(() => isSessionEndingRef.current, []);

  // ─── State ────────────────────────────────────────────────────
  const [modalState, setModalState] = useState<IdleModalState>(() => {
    return getInitialModalState(isPatientNavigator);
  });
  // Initialize with the correct value synchronously so the activity effect never
  // writes sessionExpirySeconds with a stale default before the role-aware effect fires.
  const [idleTimeout, setIdleTimeout] = useState(() =>
    resolveIdleTimeout(isPatientNavigator, ENV_IDLE_TIMEOUT, ENV_PATIENT_NAVIGATOR_IDLE_TIMEOUT),
  );
  const [countdownSeconds, setCountdownSeconds] = useState(COUNTDOWN_SECONDS); // seconds; overwritten by env var on mount
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS); // live display value, ticks down each second

  // ─── Refs ─────────────────────────────────────────────────────
  const idleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasRestoredRef = useRef(false);
  // Snapshot the persisted countdown phase/deadline once at first render, before
  // any effect (e.g. the role-aware effect that resets navigators to ACTIVE) can
  // clear it. Used by the reconcile effect to resume or expire the countdown.
  const initialCountdownSnapshotRef = useRef<{ phase: string | null; deadline: number | null }>(
    (() => {
      try {
        const phase = localStorage.getItem(IDLE_PHASE_KEY);
        const rawDeadline = localStorage.getItem(COUNTDOWN_DEADLINE_KEY);
        return { phase, deadline: rawDeadline ? Number(rawDeadline) : null };
      } catch {
        return { phase: null, deadline: null };
      }
    })(),
  );

  // ─── Helpers ──────────────────────────────────────────────────

  // Keeps React state + localStorage in sync atomically
  const updateModalState = useCallback((next: IdleModalState) => {
    setModalState(next);
    try {
      if (next === IDLE_MODAL_STATES.ACTIVE) {
        localStorage.removeItem(IDLE_PHASE_KEY);
        localStorage.removeItem(COUNTDOWN_DEADLINE_KEY);
        localStorage.removeItem(COUNTDOWN_DEADLINE_READABLE_KEY);
      } else {
        localStorage.setItem(IDLE_PHASE_KEY, next);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const clearTimers = useCallback(() => {
    if (idleRef.current) {
      clearTimeout(idleRef.current);
      idleRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  /** Clear session, remove local storage items, and hard-redirect. */
  const endSessionAndRedirect = useCallback(
    async (showTimeoutModal = false) => {
      // Signal that session clearing is in progress so unmounting forms with
      // flushOnUnmount:true do not re-populate sessionData after clearSession().
      isSessionEndingRef.current = true;
      clearTimers();
      stopTimer();

      try {
        localStorage.removeItem(LAST_ACTIVITY_KEY);
        localStorage.removeItem(LAST_ACTIVITY_READABLE_KEY);
        localStorage.removeItem(SESSION_EXPIRY_KEY);
        localStorage.removeItem(COUNTDOWN_DEADLINE_KEY);
        localStorage.removeItem(COUNTDOWN_DEADLINE_READABLE_KEY);
      } catch {
        /* ignore */
      }

      // Unfreeze any held appointment before clearing session data.
      // This covers all exit paths: End session, Later/Close, and countdown expiry.
      if (hasAppointmentHold && data.userUuid) {
        try {
          await unfreezeAppointment(data.userUuid);
        } catch (error) {
          ClientLogsService.logError(
            data.userUuid,
            "IdleTimerContext.endSessionAndRedirect",
            "Failed to unfreeze appointment",
            error instanceof Error ? error : new Error("Unknown error"),
          );
        }
      }

      const redirectTo = data.isReferrerFlow ? APP_ROUTES.REFER : APP_ROUTES.GET_STARTED;
      logAction(
        data.userUuid,
        "endSessionAndRedirect",
        `Idle session ended — redirecting to ${redirectTo}`,
      );

      clearSession();

      updateModalState(showTimeoutModal ? IDLE_MODAL_STATES.TIMEOUT : IDLE_MODAL_STATES.ACTIVE);

      setBypassBeforeUnload(true);

      // If already on the target page, do a full reload so component local state
      // (e.g. ReferForm / GetStartedForm) is fully reset — navigate() alone won't remount.
      if (pathname === redirectTo) {
        window.location.reload();
      } else {
        navigate(redirectTo, { replace: true });
        // Reset after unmount cleanups have run so future back-navigation flushes work normally.
        setTimeout(() => {
          isSessionEndingRef.current = false;
        }, 0);
      }
    },
    [
      clearTimers,
      stopTimer,
      hasAppointmentHold,
      data.userUuid,
      data.isReferrerFlow,
      pathname,
      clearSession,
      updateModalState,
      navigate,
      LAST_ACTIVITY_KEY,
      LAST_ACTIVITY_READABLE_KEY,
      SESSION_EXPIRY_KEY,
    ],
  );

  /** Start the visible countdown on the warning modal.
   *  The countdown is anchored to an absolute deadline persisted in localStorage
   *  so it survives a tab close/reopen or provider remount. Pass an existing
   *  deadline to resume; omit it to start a fresh countdown. */
  const startCountdown = useCallback(
    (deadline?: number) => {
      // Guard: clear any existing countdown first (React StrictMode safety)
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }

      const endAt = deadline ?? Date.now() + countdownSeconds * 1000;
      try {
        localStorage.setItem(COUNTDOWN_DEADLINE_KEY, String(endAt));
        localStorage.setItem(COUNTDOWN_DEADLINE_READABLE_KEY, toReadableTimestamp(endAt));
      } catch {
        /* ignore */
      }

      const tick = () => {
        const remaining = Math.ceil((endAt - Date.now()) / 1000);
        if (remaining <= 0) {
          clearInterval(countdownRef.current!);
          countdownRef.current = null;
          endSessionAndRedirect(true);
        } else {
          setCountdown(remaining);
        }
      };

      tick(); // set initial display value immediately
      countdownRef.current = setInterval(tick, 1000);
    },
    [endSessionAndRedirect, countdownSeconds],
  );

  // ─── Core idle callbacks ─────────────────────────────────────

  /** Called when the idle timeout fires — transitions to warning state. */
  const showWarning = useCallback(() => {
    logger.info({
      text: "User idle — showing warning modal",
      location: "IdleTimerContext.showWarning",
    });
    logAction(data.userUuid, "showWarning", 'Showing "Still here?" modal with countdown.');

    updateModalState(IDLE_MODAL_STATES.WARNING);
    startCountdown();
  }, [data.userUuid, startCountdown, updateModalState]);

  /** Reset the idle timer on user activity. Only runs while modal is not showing. */
  const scheduleIdleTimeout = useCallback(() => {
    if (idleRef.current) clearTimeout(idleRef.current);
    idleRef.current = setTimeout(showWarning, idleTimeout * 1000);
  }, [showWarning, idleTimeout]);

  // ─── Effects ──────────────────────────────────────────────────

  // Set isReferrerFlow in ApplicationContext when on /refer or /refer/:parentOrganization
  useEffect(() => {
    if (pathname.startsWith(APP_ROUTES.REFER) && !data.isReferrerFlow) {
      setData("isReferrerFlow", true);
    }
  }, [pathname, data.isReferrerFlow, setData]);

  // Load countdown config from centralized environment context.
  // idleTimeout is owned by the role-aware effect below; only countdownSeconds is set here.
  useEffect(() => {
    if (isValidPositiveNumber(ENV_COUNTDOWN_SECONDS)) {
      setCountdownSeconds(ENV_COUNTDOWN_SECONDS);
    }
  }, [ENV_COUNTDOWN_SECONDS]);

  // Patient Navigators use a 12-hour idle timeout instead of the default 5 minutes.
  // Uses updateModalState to keep React state and localStorage in sync atomically:
  // resetting to ACTIVE clears any stale "timeout" idlePhase from both stores.
  useEffect(() => {
    setIdleTimeout(
      resolveIdleTimeout(isPatientNavigator, ENV_IDLE_TIMEOUT, ENV_PATIENT_NAVIGATOR_IDLE_TIMEOUT),
    );
    if (isPatientNavigator) {
      updateModalState(IDLE_MODAL_STATES.ACTIVE);
    }
  }, [isPatientNavigator, ENV_IDLE_TIMEOUT, ENV_PATIENT_NAVIGATOR_IDLE_TIMEOUT, updateModalState]);

  // Attach / detach activity listeners while user is active
  useEffect(() => {
    if (isIdleTimerDisabled || modalState !== IDLE_MODAL_STATES.ACTIVE) return;

    scheduleIdleTimeout(); // Start initial timer

    // Write baseline on activation so closed-browser detection has a timestamp
    // even if the user never triggers a DOM event before closing.
    // Only idleTimeout (not + countdownSeconds) is used — the countdown grace period
    // is irrelevant for a closed browser; it only applies when the warning modal is visible.
    let lastStorageWriteAt: number | null = null;
    const writeActivitySnapshot = () => {
      // Skip if session ending is in progress — the End-session click bubbles to
      // this document listener and would otherwise re-write the keys that
      // endSessionAndRedirect() just removed.
      if (isSessionEndingRef.current) return;
      const now = Date.now();
      // Throttle localStorage writes because mousemove/scroll can fire many times per second.
      if (lastStorageWriteAt !== null && now - lastStorageWriteAt < 1000) return;
      lastStorageWriteAt = now;
      try {
        localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
        localStorage.setItem(LAST_ACTIVITY_READABLE_KEY, toReadableTimestamp(now));
        localStorage.setItem(SESSION_EXPIRY_KEY, String(idleTimeout));
      } catch {
        /* ignore SSR / private browsing */
      }
    };

    writeActivitySnapshot();

    const onActivity = () => {
      scheduleIdleTimeout();
      writeActivitySnapshot();
    };
    ACTIVITY_EVENTS.forEach((e) => {
      document.addEventListener(e, onActivity, { passive: true });
    });

    return () => {
      if (idleRef.current) {
        clearTimeout(idleRef.current);
        idleRef.current = null;
      }
      ACTIVITY_EVENTS.forEach((e) => {
        document.removeEventListener(e, onActivity);
      });
    };
  }, [
    isIdleTimerDisabled,
    modalState,
    scheduleIdleTimeout,
    idleTimeout,
    LAST_ACTIVITY_KEY,
    LAST_ACTIVITY_READABLE_KEY,
    SESSION_EXPIRY_KEY,
  ]);

  // Reconcile a persisted warning/confirmation countdown on mount. This covers
  // page refresh AND tab close/reopen: the deadline is read directly from
  // localStorage (independent of getInitialModalState, which never restores
  // "warning"), so a countdown that elapsed while the tab was closed still ends
  // the session — including for patient navigators whose closed-browser expiry
  // window is otherwise 12 hours.
  useEffect(() => {
    if (hasRestoredRef.current || isIdleTimerDisabled) return;

    const { phase: storedPhase, deadline: storedDeadline } = initialCountdownSnapshotRef.current;

    const isCountdownPhase =
      storedPhase === IDLE_MODAL_STATES.WARNING || storedPhase === IDLE_MODAL_STATES.CONFIRMATION;

    if (!isCountdownPhase || !storedDeadline || Number.isNaN(storedDeadline)) return;

    hasRestoredRef.current = true;

    if (Date.now() >= storedDeadline) {
      // Countdown already elapsed while the tab was closed → end the session now.
      endSessionAndRedirect(true);
    } else {
      // Time remains → re-show the warning and resume for the remaining seconds.
      updateModalState(storedPhase as IdleModalState);
      startCountdown(storedDeadline);
    }
  }, [isIdleTimerDisabled, startCountdown, endSessionAndRedirect, updateModalState]);

  // Cleanup on unmount
  useEffect(() => clearTimers, [clearTimers]);

  // ─── User action handlers ─────────────────────────────────────

  const onStayInSession = useCallback(() => {
    // Flush any in-progress form state to ApplicationContext before resuming
    try {
      flushCallbackRef.current?.();
    } catch (err) {
      ClientLogsService.logError(
        data.userUuid || "unknown",
        "IdleTimerContext.onStayInSession",
        "Form flush callback threw during idle-modal resume",
        err instanceof Error ? err : new Error(String(err)),
      );
    }

    clearTimers();
    logClick(data.userUuid, "idle-modal-im-here-btn", "User clicked I'm here");

    if (hasAppointmentHold) {
      updateModalState(IDLE_MODAL_STATES.CONFIRMATION);
      startCountdown(); // Start a fresh countdown for the confirmation modal
    } else {
      updateModalState(IDLE_MODAL_STATES.ACTIVE);
    }
  }, [clearTimers, data.userUuid, hasAppointmentHold, startCountdown, updateModalState]);

  const onEndSession = useCallback(() => {
    logClick(data.userUuid, "idle-modal-end-session-btn", "User clicked End session");
    endSessionAndRedirect();
  }, [data.userUuid, endSessionAndRedirect]);

  const onReReserve = useCallback(async () => {
    logClick(data.userUuid, "idle-modal-re-reserve-btn", "User clicked Re-reserve appointment");
    stopTimer();

    if (data.userUuid) {
      try {
        await unfreezeAppointment(data.userUuid);
      } catch (error) {
        ClientLogsService.logError(
          data.userUuid,
          "IdleTimerContext.onReReserve",
          "Failed to unfreeze appointment",
          error instanceof Error ? error : new Error("Unknown error"),
        );
      }
    }

    clearAppointmentSelection();
    updateModalState(IDLE_MODAL_STATES.ACTIVE);
    setBypassBeforeUnload(true);
    navigate(resolveProviderSelectionPath(data.providerBackPath));
  }, [
    data.providerBackPath,
    data.userUuid,
    clearAppointmentSelection,
    stopTimer,
    updateModalState,
    navigate,
  ]);

  const onDismissAndEndSession = useCallback(() => {
    logClick(
      data.userUuid,
      "idle-modal-later-close-btn",
      "User declined re-reserve — ending session",
    );
    endSessionAndRedirect();
  }, [data.userUuid, endSessionAndRedirect]);

  const onAcknowledgeTimeout = useCallback(() => {
    logClick(data.userUuid, "idle-modal-timeout-continue-btn", "User acknowledged session timeout");
    updateModalState(IDLE_MODAL_STATES.ACTIVE);
  }, [data.userUuid, updateModalState]);

  // ─── Context value ─────────────────────────────────────────────

  const value = useMemo(() => {
    return {
      modalState,
      countdown,
      isIdleTimerDisabled,
      hasAppointmentHold,
      onStayInSession,
      onEndSession,
      onReReserve,
      onDismissAndEndSession,
      onAcknowledgeTimeout,
      registerFlushCallback,
      unregisterFlushCallback,
      checkIsSessionEnding,
    };
  }, [
    modalState,
    countdown,
    isIdleTimerDisabled,
    hasAppointmentHold,
    onStayInSession,
    onEndSession,
    onReReserve,
    onDismissAndEndSession,
    onAcknowledgeTimeout,
    registerFlushCallback,
    unregisterFlushCallback,
    checkIsSessionEnding,
  ]);

  return <IdleTimerContext.Provider value={value}>{children}</IdleTimerContext.Provider>;
};

export const useIdleTimerContext = () => {
  const context = useContext(IdleTimerContext);
  if (!context) {
    throw new Error("useIdleTimerContext must be used within an IdleTimerProvider");
  }
  return context;
};
